import React, { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Clock, MapPin, ChevronRight } from "lucide-react";
import { API, HERO_BG, PageHero } from "../shared";

const EVENT_CAROUSEL_IMAGES = [
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/iae211zi_typorama.png",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/ta58pels_Artboard%205.jpeg",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/eym9ei2p_IMG_2205.jpeg",
];

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    axios.get(`${API}/events`).then(res => { setEvents(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % EVENT_CAROUSEL_IMAGES.length), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="pt-20 fade-in">
      <PageHero eyebrow="What's Happening" title="Events" italic bg={HERO_BG.events} testId="events-title" />

      <section className="bg-black" data-testid="events-carousel">
        <div className="max-w-5xl mx-auto">
          <div className="relative aspect-square md:aspect-[4/3] lg:aspect-[16/10] overflow-hidden bg-black">
            {EVENT_CAROUSEL_IMAGES.map((src, i) => (
              <img key={src} src={src} alt={`Featured event ${i + 1}`}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ease-in-out ${i === slide ? "opacity-100" : "opacity-0"}`}
                data-testid={`carousel-slide-${i}`}
              />
            ))}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-10">
              {EVENT_CAROUSEL_IMAGES.map((src, i) => (
                <button key={src} onClick={() => setSlide(i)} aria-label={`Go to slide ${i + 1}`}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${i === slide ? "bg-white w-8" : "bg-white/40 hover:bg-white/70"}`}
                  data-testid={`carousel-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black border-t border-white/10 py-16" data-testid="we-must-cta">
        <div className="max-w-5xl mx-auto px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="heading-caps text-xs text-red-500 mb-3 tracking-[0.2em]">We Must Conference</p>
            <h2 className="text-3xl md:text-4xl text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Secure your seat at <span className="italic">We Must</span>.
            </h2>
            <p className="text-white/50 mt-3 text-sm md:text-base">Tickets are going fast — reserve yours through Eventbrite.</p>
          </div>
          <a href="https://www.eventbrite.com/e/we-must-conference-tickets-1986397738107?msockid=104ab7eb82e666fd1346a154838a67e4"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-red-600 hover:text-white transition-colors duration-300 whitespace-nowrap"
            data-testid="we-must-register-btn"
          >Register Today<ChevronRight size={16} /></a>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-5xl mx-auto px-8">
          {loading ? (
            <div className="text-center py-12"><div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto"></div></div>
          ) : events.length > 0 ? (
            <div className="space-y-0" data-testid="events-list">
              {events.map((event, index) => (
                <div key={event.event_id} className={`flex flex-col md:flex-row gap-8 py-12 ${index !== events.length - 1 ? 'border-b border-black/10' : ''}`} data-testid={`event-${event.event_id}`}>
                  <div className="md:w-32 flex-shrink-0">
                    <p className="text-5xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>{new Date(event.date).getDate()}</p>
                    <p className="text-sm text-black/40 uppercase tracking-wider">{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</p>
                  </div>
                  <div className="flex-grow">
                    <p className="heading-caps text-[10px] text-black/40 mb-2 tracking-[0.2em]">{event.event_type.replace(/_/g, ' ')}</p>
                    <h3 className="text-2xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>{event.title}</h3>
                    <p className="text-black/50 mb-4">{event.description}</p>
                    <div className="flex flex-wrap gap-6 text-sm text-black/40">
                      <div className="flex items-center gap-2"><Clock size={14} />{new Date(event.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
                      <div className="flex items-center gap-2"><MapPin size={14} />{event.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-neutral-100">
              <Calendar size={48} className="mx-auto text-black/20 mb-4" />
              <p className="text-black/40">No upcoming events</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
